import { Card } from '../Models/Card'
import { CardsState } from '../Models/CardsState'
import { paint, schedulePaint, cancelScheduledPaint } from './paint'
import { appState, getCurrentCardState, cardState, undo } from './state'
import { saveGameState } from './stateManager'
import { assignCardCoordinates } from './assignCardCoordinates'
import {
    setAnimationPosition,
    clearAnimationPosition,
    clearAllAnimationPositions,
    getCardPosition,
} from './cardPositions'
import { getAllCards } from './gameRules'

// ─── Easing Functions ────────────────────────────────────────────────

const easings = {
    linear: (t: number) => t,
    easeOutQuad: (t: number) => t * (2 - t),
    easeOutCubic: (t: number) => 1 - Math.pow(1 - t, 3),
    easeInOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
    easeOutBack: (t: number) => {
        const c1 = 1.70158
        const c3 = c1 + 1
        return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)
    },
} as const

type EasingFunction = (t: number) => number

// ─── Animation Types ─────────────────────────────────────────────────

interface CardAnimation {
    card: Card
    startX: number
    startY: number
    endX: number
    endY: number
    startTime: number
    duration: number
    easing: EasingFunction
    onComplete?: () => void
}

// ─── Unique Card Identifier ─────────────────────────────────────────

/** Unique key for a card: value + type (e.g. "KS", "AH", "10D"). */
function cardKey(card: Card): string {
    return card.value + card.type
}

// ─── Animation Manager ──────────────────────────────────────────────

class AnimationManagerClass {
    private activeAnimations: CardAnimation[] = []
    private rafId: number | null = null
    private running = false

    /**
     * Queue an animation for a card.
     *
     * The card's x/y (home position) should already be set to the FINAL position
     * (via assignCardCoordinates). The animation writes interpolated positions
     * to the overlay map; it never mutates card.x/y.
     */
    queueAnimation(animation: CardAnimation): void {
        // If animations are disabled, just fire onComplete immediately
        if (!appState.settings.animationsEnabled) {
            if (animation.onComplete) {
                animation.onComplete()
            }
            return
        }

        // Set the initial animation overlay so the very first paint sees the
        // start position (not the home position which is the end position).
        setAnimationPosition(animation.card, animation.startX, animation.startY)

        this.activeAnimations.push(animation)

        // Start the loop if not already running.
        if (!this.running) {
            this.running = true
            // Cancel any pending schedulePaint RAF to prevent it from painting
            // before tick has a chance to interpolate animation positions.
            cancelScheduledPaint()
            this.rafId = requestAnimationFrame(this.tick)
        }
    }

    /**
     * Animate a card from its pre-move position to its current home position.
     * Convenience helper — reads card.x/y as endX/endY (the home position
     * set by assignCardCoordinates).
     */
    animateCard(
        card: Card,
        fromX: number,
        fromY: number,
        duration: number = 250,
        easing: EasingFunction = easings.easeOutCubic,
        onComplete?: () => void
    ): void {
        this.queueAnimation({
            card,
            startX: fromX,
            startY: fromY,
            endX: card.x,
            endY: card.y,
            startTime: performance.now(),
            duration,
            easing,
            onComplete,
        })
    }

    /**
     * Animate multiple cards from saved positions to their current home positions.
     * Each entry is { card, fromX, fromY }.
     */
    animateCards(
        cards: { card: Card; fromX: number; fromY: number }[],
        duration: number = 250,
        easing: EasingFunction = easings.easeOutCubic,
        onAllComplete?: () => void
    ): void {
        let completed = 0
        const total = cards.length

        if (total === 0) {
            if (onAllComplete) onAllComplete()
            return
        }

        for (const entry of cards) {
            this.animateCard(entry.card, entry.fromX, entry.fromY, duration, easing, () => {
                completed++
                if (completed === total && onAllComplete) {
                    onAllComplete()
                }
            })
        }
    }

    /**
     * Check if any animations are currently active.
     */
    isAnimating(): boolean {
        return this.activeAnimations.length > 0
    }

    /**
     * Cancel all active animations, snapping to final positions.
     * We simply clear the overlays — card.x/y already hold the correct
     * final positions (set by assignCardCoordinates before animation started).
     */
    clearAll(): void {
        for (const anim of this.activeAnimations) {
            // Remove overlay so the card renders at its home position (card.x/y)
            clearAnimationPosition(anim.card)
            if (anim.onComplete) {
                anim.onComplete()
            }
        }
        this.activeAnimations = []
        this.stop()
    }

    /**
     * The core animation loop. Writes interpolated positions to the overlay map.
     * Never mutates card.x or card.y.
     */
    private tick = (): void => {
        if (!this.running) return

        const now = performance.now()
        const completedAnimations: CardAnimation[] = []

        for (const anim of this.activeAnimations) {
            const elapsed = now - anim.startTime
            const progress = Math.min(elapsed / anim.duration, 1)
            const easedProgress = anim.easing(progress)

            if (progress >= 1) {
                // Animation complete — remove overlay so card renders at home position
                clearAnimationPosition(anim.card)
                completedAnimations.push(anim)
            } else {
                // Interpolate position into the overlay
                const x = anim.startX + (anim.endX - anim.startX) * easedProgress
                const y = anim.startY + (anim.endY - anim.startY) * easedProgress
                setAnimationPosition(anim.card, x, y)
            }
        }

        // Remove completed animations and fire callbacks
        for (const completed of completedAnimations) {
            const index = this.activeAnimations.indexOf(completed)
            if (index !== -1) {
                this.activeAnimations.splice(index, 1)
            }
            if (completed.onComplete) {
                completed.onComplete()
            }
        }

        // Repaint
        paint()

        // Continue loop or stop
        if (this.activeAnimations.length > 0) {
            this.rafId = requestAnimationFrame(this.tick)
        } else {
            this.stop()
        }
    }

    private stop(): void {
        this.running = false
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId)
            this.rafId = null
        }
        // Ensure all overlays are cleaned up when animation loop stops
        clearAllAnimationPositions()
    }
}

// Singleton
export const animationManager = new AnimationManagerClass()

// ─── Animated Undo ──────────────────────────────────────────────────

/**
 * Build a position map from a CardsState: maps unique card key → rendered { x, y }.
 * Uses getCardPosition() so it captures animation/drag overlays if active.
 */
function buildPositionMap(state: CardsState): Map<string, { x: number; y: number }> {
    const map = new Map<string, { x: number; y: number }>()

    for (const card of state.stock) {
        const pos = getCardPosition(card)
        map.set(cardKey(card), { x: pos.x, y: pos.y })
    }
    for (const card of state.waste) {
        const pos = getCardPosition(card)
        map.set(cardKey(card), { x: pos.x, y: pos.y })
    }
    for (const pile of state.tableau) {
        for (const card of pile) {
            const pos = getCardPosition(card)
            map.set(cardKey(card), { x: pos.x, y: pos.y })
        }
    }
    for (const pile of state.foundation) {
        for (const card of pile) {
            const pos = getCardPosition(card)
            map.set(cardKey(card), { x: pos.x, y: pos.y })
        }
    }

    return map
}

/**
 * Perform an animated undo: pop state, then animate cards that changed position
 * from their old position (in the popped state) to their new position (in the now-current state).
 */
export function animatedUndo(): void {
    if (cardState.length <= 1) return

    // Capture rendered positions from the state we're about to pop
    const stateBeforeUndo = getCurrentCardState()
    const beforePositions = buildPositionMap(stateBeforeUndo)

    // Pop the state
    undo()

    // Recalculate home positions on the revealed state to ensure they match
    // current screen dimensions.
    const revealedState = getCurrentCardState()
    const recalculated = assignCardCoordinates(revealedState)
    cardState[cardState.length - 1] = recalculated
    saveGameState(cardState)

    // If animations are disabled, just repaint
    if (!appState.settings.animationsEnabled) {
        schedulePaint()
        return
    }

    // Find cards that moved — compare old rendered position to new home position
    const currentState = getCurrentCardState()
    const allCards = getAllCards(currentState)

    const animEntries: { card: Card; fromX: number; fromY: number }[] = []

    for (const card of allCards) {
        const oldPos = beforePositions.get(cardKey(card))
        if (oldPos && (Math.abs(oldPos.x - card.x) > 1 || Math.abs(oldPos.y - card.y) > 1)) {
            animEntries.push({
                card,
                fromX: oldPos.x,
                fromY: oldPos.y,
            })
        }
    }

    if (animEntries.length > 0) {
        animationManager.animateCards(animEntries, 200)
    } else {
        schedulePaint()
    }
}
